import { AxiosError } from "axios";

/**
 * Renter-facing failure copy.
 *
 * Every `catch` in `src/api/**` used to invent its own string, and they were
 * written from the developer's seat: "An error occurred during fetcing
 * booking.", "An unknown error occurred.", "An error occurred during fetching
 * invoince." Those reach the renter verbatim through `toast.error(...)`. They
 * name our internal operation, they don't say what happened, and they never
 * say what to do next.
 *
 * This module is the single place that decides what a renter sees. Call sites
 * pass the caught error plus the area of the site they're in; they no longer
 * write copy.
 *
 * Three rules the copy follows:
 *   1. Say what happened in the renter's terms — their booking, their card,
 *      their dates. Never our endpoint, model, or provider.
 *   2. Say whether it's their side or ours. "Check your connection" and "this
 *      is on us" lead to very different renter behaviour.
 *   3. Give the next step. A dead end with no action is what makes an error
 *      feel broken rather than handled.
 */

export type ErrorDomain =
  | "auth"
  | "booking"
  | "cancellation"
  | "company"
  | "coupon"
  | "insurance"
  | "invoice"
  | "locations"
  | "payment"
  | "photos"
  | "profile"
  | "agreement"
  | "vehicles"
  | "generic";

/**
 * Copy follows the conventions renters already know from everywhere else on
 * the web, rather than anything bespoke to this product. "Something went
 * wrong. Please try again." is understood instantly precisely because it is
 * everywhere; a cleverer sentence only makes the reader work harder. Same for
 * "Your session has expired", "No internet connection", "Too many attempts".
 *
 * Where we can be specific about the object — booking, car, promo code — we
 * are, because "Booking not found" beats "Not found". Everything else stays on
 * the standard phrasing.
 */

/** Names the thing that failed, for the generic "couldn't load X" sentence. */
const DOMAIN_SUBJECT: Record<ErrorDomain, string> = {
  auth: "sign you in",
  booking: "complete your booking",
  cancellation: "cancel your booking",
  company: "load this page",
  coupon: "apply your promo code",
  insurance: "load insurance options",
  invoice: "load your receipt",
  locations: "load pick-up locations",
  payment: "process your payment",
  photos: "upload your photos",
  profile: "save your details",
  agreement: "load your rental agreement",
  vehicles: "load available cars",
  generic: "complete that",
};

/** Shown when the record genuinely isn't there (404). */
const DOMAIN_MISSING: Record<ErrorDomain, string> = {
  auth: "No account found with those details. Please check your email address and try again.",
  booking: "Booking not found. Please check the link in your confirmation email and try again.",
  cancellation: "Booking not found. It may already have been cancelled.",
  company: "Page not found.",
  coupon: "Invalid promo code. Please check the code and try again.",
  insurance: "Insurance options are unavailable for this car.",
  invoice: "Receipt not found. It may take a few minutes to appear after payment.",
  locations: "Couldn't load pick-up locations. Please try again.",
  payment: "Payment details not found. Please enter your card again.",
  photos: "Photos not found. Please try uploading them again.",
  profile: "Account details not found. Please sign in again.",
  agreement: "Your rental agreement isn't available yet.",
  vehicles: "Car not found. It may no longer be available for your dates.",
  generic: "Not found.",
};

const CONNECTION =
  "No internet connection. Please check your connection and try again.";

const TIMEOUT = "The request timed out. Please try again.";

const SERVER = "Something went wrong on our end. Please try again later.";

const SESSION = "Your session has expired. Please sign in again.";

const FORBIDDEN = "You don't have permission to view this.";

const RATE_LIMITED = "Too many attempts. Please wait a moment and try again.";

const TOO_LARGE = "File is too large. Please choose a file under 10MB.";

/**
 * Backend validation messages are often genuinely the most useful thing we can
 * show — "This coupon has expired", "These dates overlap an existing booking".
 * But the same field also carries operator- and developer-voiced text that
 * means nothing to a renter and, worse, exposes how the system is wired:
 * "Company does not have an active Stripe account." is a real response from
 * our own checkout.
 *
 * So server text is shown only if it reads like something written for the
 * person holding the phone. Anything mentioning our plumbing, or shaped like a
 * stack trace or a serializer dump, is dropped for the domain default.
 */
const OPERATOR_VOCABULARY =
  /\b(compan(y|ies)|merchant|stripe|square|bonzah|canopy|vouched|webhook|api[\s_-]?key|secret|token|credential|serializer|queryset|admin|dashboard|tenant|backend|endpoint|database|migration|celery|s3|bucket)\b/i;

const LOOKS_TECHNICAL =
  /(traceback|exception|stack|\bnull\b|\bundefined\b|\bNoneType\b|DoesNotExist|KeyError|ValueError|TypeError|IntegrityError|<[a-z/!]|[{}[\]]|https?:\/\/|\bat\s+\w+\.\w+|_id\b|__)/i;

/**
 * Provider object identifiers — `pm_1Ttj…`, `pi_…`, `req_…`. Stripe puts these
 * in otherwise readable sentences ("No such PaymentMethod: 'pm_1Ttj…'"), which
 * clears every check above and lands a database key in front of the renter.
 * Seen for real in 1NOW-1396.
 */
const PROVIDER_OBJECT_ID =
  /\b(pm|pi|cus|ch|sub|seti|acct|req|tok|src|price|prod|inv)_[A-Za-z0-9]{6,}/;

/**
 * Deployment diagnostics that ride along on genuine decline messages: "Your
 * card was declined. Your request was in live mode, but used a known test
 * card." The first sentence is exactly what the renter needs; the rest is our
 * configuration leaking into checkout (1NOW-1254). Trimmed rather than
 * dropped, so the useful half survives.
 */
const DIAGNOSTIC_TAIL =
  /\s*(your request was in (live|test) mode[^.]*\.?|but used a known (test|live) card\.?)/gi;

/**
 * Field-level validation with no field named. True, and useless — the renter
 * cannot tell which input is wrong, so the domain default is more helpful.
 */
const CONTEXTLESS_VALIDATION =
  /^this field (is required|may not be (blank|null))\.?$/i;

/**
 * Judge a server string on whether a renter could read it and know what to do.
 */
const isRenterSafe = (message: unknown): message is string => {
  if (typeof message !== "string") return false;
  const text = message.trim();
  if (text.length < 3) return false;
  // Long strings are almost always dumps rather than sentences.
  if (text.length > 200) return false;
  if (CONTEXTLESS_VALIDATION.test(text)) return false;
  if (PROVIDER_OBJECT_ID.test(text)) return false;
  if (OPERATOR_VOCABULARY.test(text)) return false;
  if (LOOKS_TECHNICAL.test(text)) return false;
  return true;
};

/** Drop deployment diagnostics, keeping the part written for the renter. */
const trimDiagnostics = (text: string): string =>
  text.replace(DIAGNOSTIC_TAIL, "").replace(/\s{2,}/g, " ").trim();

/** Ensure whatever we show ends as a sentence — server text often doesn't. */
const asSentence = (text: string): string => {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/**
 * Pull the message out of the several shapes our API returns it in:
 * `{ error }`, `{ message }`, `{ detail }`, and DRF's field-keyed
 * `{ field: ["..."] }`.
 */
const serverMessage = (data: any): string | undefined => {
  if (!data) return undefined;
  if (typeof data === "string") return data;

  const direct = data.error ?? data.message ?? data.detail;
  if (typeof direct === "string") return direct;
  if (Array.isArray(direct) && typeof direct[0] === "string") return direct[0];

  // DRF field errors: take the first one rather than naming every field, which
  // is what made the contact form shout about all four inputs at once.
  const firstKey = Object.keys(data)[0];
  const value = firstKey ? data[firstKey] : undefined;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];

  return undefined;
};

/**
 * Turn any caught failure into a sentence a renter can act on.
 *
 * @param error  whatever landed in the `catch`
 * @param domain the part of the site the renter is in
 */
export const renterError = (
  error: unknown,
  domain: ErrorDomain = "generic",
): string => {
  const fallback = `Couldn't ${DOMAIN_SUBJECT[domain]}. Please try again.`;

  const axiosError = error as AxiosError<any> | undefined;
  const isAxios =
    !!axiosError && (axiosError as any).isAxiosError === true;

  if (!isAxios) return fallback;

  // No response at all: offline, DNS, CORS, or the request timed out.
  if (!axiosError.response) {
    if (axiosError.code === "ECONNABORTED") return TIMEOUT;
    return CONNECTION;
  }

  const { status, data } = axiosError.response;

  if (status === 401) return SESSION;
  if (status === 403) return FORBIDDEN;
  if (status === 404) return DOMAIN_MISSING[domain];
  if (status === 413) return TOO_LARGE;
  if (status === 429) return RATE_LIMITED;
  if (status >= 500) return SERVER;

  // 400/409/422 — the server usually has something specific and useful here,
  // but only if it was written for a renter.
  const raw = serverMessage(data);
  const message = typeof raw === "string" ? trimDiagnostics(raw) : raw;
  if (isRenterSafe(message)) return asSentence(message);

  return fallback;
};

export default renterError;
